"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.observe = observe;
exports.unobserve = unobserve;
exports.destroy = destroy;
var INSTANCE_MAP = new Map();
var OBSERVER_MAP = new Map();

/**
 * Monitor element, and trigger callback when element becomes visible
 * @param element {HTMLElement}
 * @param callback {Function} Called with inView
 * @param options {Object} InterSection observer options
 * @param options.threshold {Number} Number between 0 and 1, indicating how much of the element should be visible before triggering
 * @param options.root {HTMLElement} It should have a unique id or data-intersection-id in order for the Observer to reused.
 * @param options.rootMargin {String} The CSS margin to apply to the root element.
 * @param rootId {String} Unique identifier for the root element, to enable reusing the IntersectionObserver
 */
function observe(element, callback) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    threshold: 0
  };
  var rootId = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  var threshold = options.threshold,
      root = options.root,
      rootMargin = options.rootMargin;

  if (!element || !callback) return;
  var observerId = rootMargin ? threshold + "_" + rootMargin : "" + threshold;

  if (root) {
    observerId = rootId ? rootId + "_" + observerId : null;
  }

  var observerInstance = observerId ? OBSERVER_MAP.get(observerId) : null;
  if (!observerInstance) {
    observerInstance = new IntersectionObserver(onChange, options);
    if (observerId) OBSERVER_MAP.set(observerId, observerInstance);
  }

  var instance = {
    callback: callback,
    visible: false,
    options: options,
    observerId: observerId,
    observer: !observerId ? observerInstance : undefined
  };

  INSTANCE_MAP.set(element, instance);

  observerInstance.observe(element);

  return instance;
}

/**
 * Stop observing an element. If an element is removed from the DOM or otherwise destroyed,
 * make sure to call this method.
 * @param element {HTMLElement}
 */
function unobserve(element) {
  if (!element) return;

  if (INSTANCE_MAP.has(element)) {
    var _INSTANCE_MAP$get = INSTANCE_MAP.get(element),
        observerId = _INSTANCE_MAP$get.observerId,
        observer = _INSTANCE_MAP$get.observer;

    var observerInstance = observerId ? OBSERVER_MAP.get(observerId) : observer;

    if (observerInstance) {
      observerInstance.unobserve(element);
    }

    // Check if we are stilling observing any elements with the same threshold.
    var itemsLeft = false;
    if (observerId) {
      INSTANCE_MAP.forEach(function (item, key) {
        if (item.observerId === observerId && key !== element) {
          itemsLeft = true;
        }
      });
    }

    if (observerInstance && !itemsLeft) {
      // No more elements to observe for threshold, disconnect observer
      observerInstance.disconnect();
      OBSERVER_MAP.delete(observerId);
    }

    // Remove reference to element
    INSTANCE_MAP.delete(element);
  }
}

/**
 * Destroy all IntersectionObservers currently connected
 **/
function destroy() {
  OBSERVER_MAP.forEach(function (observer) {
    observer.disconnect();
  });

  OBSERVER_MAP.clear();
  INSTANCE_MAP.clear();
}

function onChange(changes) {
  changes.forEach(function (intersection) {
    if (INSTANCE_MAP.has(intersection.target)) {
      var isIntersecting = intersection.isIntersecting,
          intersectionRatio = intersection.intersectionRatio,
          target = intersection.target;

      var instance = INSTANCE_MAP.get(target);
      var options = instance.options;

      var inView = void 0;

      if (Array.isArray(options.threshold)) {
        // If threshold is an array, check if any of them intersects. This just triggers the onChange event multiple times.
        inView = options.threshold.some(function (threshold) {
          return instance.visible ? intersectionRatio > threshold : intersectionRatio >= threshold;
        });
      } else {
        // Trigger on 0 ratio only when not visible. This is fallback for browsers without isIntersecting support
        inView = instance.visible ? intersectionRatio > options.threshold : intersectionRatio >= options.threshold;
      }

      if (isIntersecting !== undefined) {
        // If isIntersecting is defined, ensure that the element is actually intersecting.
        // Otherwise it reports a threshold of 0
        inView = inView && isIntersecting;
      }

      // Update the visible value on the instance
      instance.visible = inView;

      if (instance.callback) {
        instance.callback(inView);
      }
    }
  });
}

exports.default = {
  observe: observe,
  unobserve: unobserve,
  destroy: destroy
};


//////////////////
// WEBPACK FOOTER
// ./~/react-intersection-observer/es/intersection.js
// module id = 1246
// module chunks = 34